// Minimal typed loader for the Square Web Payments SDK.
// The SDK is loaded on demand (only when Square is configured) and injected
// once. Card details are entered into Square-hosted fields and tokenized
// client-side, so raw card data never touches our backend.

export interface SquareTokenResult {
  status: string;
  token?: string;
  errors?: { message: string }[];
}

export interface SquareCard {
  attach(selector: string): Promise<void>;
  tokenize(): Promise<SquareTokenResult>;
  destroy(): Promise<void>;
}

export interface SquarePayments {
  card(): Promise<SquareCard>;
}

export interface SquareSdk {
  payments(applicationId: string, locationId: string): SquarePayments;
}

declare global {
  interface Window {
    Square?: SquareSdk;
  }
}

const SDK_URLS: Record<'sandbox' | 'production', string> = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

let loadPromise: Promise<SquareSdk> | null = null;

export function loadSquareSdk(environment: 'sandbox' | 'production'): Promise<SquareSdk> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Square SDK can only load in the browser'));
  }
  if (window.Square) return Promise.resolve(window.Square);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<SquareSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URLS[environment];
    script.async = true;
    script.onload = () => {
      if (window.Square) resolve(window.Square);
      else reject(new Error('Square SDK loaded but window.Square is unavailable'));
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load the Square payment SDK'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
